import { createRouter, createWebHistory } from 'vue-router'

import AppShell from '@/layouts/AppShell.vue'
import LoginView from '@/views/LoginView.vue'
import { useAuthStore } from '@/stores/auth'
import { pinia } from '@/stores'

const DashboardView = () => import('@/views/DashboardView.vue')
const VehiclesView = () => import('@/views/VehiclesView.vue')
const VehicleDetailView = () => import('@/views/VehicleDetailView.vue')
const VehicleEditorView = () => import('@/views/VehicleEditorView.vue')
const EnergyView = () => import('@/views/EnergyView.vue')
const EnergyDetailView = () => import('@/views/EnergyDetailView.vue')
const EnergyEditorView = () => import('@/views/EnergyEditorView.vue')
const MaintenanceView = () => import('@/views/MaintenanceView.vue')
const MaintenanceDetailView = () => import('@/views/MaintenanceDetailView.vue')
const MaintenanceEditorView = () => import('@/views/MaintenanceEditorView.vue')
const InsuranceView = () => import('@/views/InsuranceView.vue')
const InsuranceDetailView = () => import('@/views/InsuranceDetailView.vue')
const InsuranceEditorView = () => import('@/views/InsuranceEditorView.vue')
const ChangePasswordView = () => import('@/views/ChangePasswordView.vue')
const SystemManagementView = () => import('@/views/SystemManagementView.vue')
const DataExchangeView = () => import('@/views/DataExchangeView.vue')

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { public: true, title: '登录' },
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: ChangePasswordView,
      meta: { title: '修改密码' },
    },
    {
      path: '/',
      component: AppShell,
      children: [
        { path: '', redirect: '/login' },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: DashboardView,
          meta: { title: '数据看板', permission: 'dashboard:read' },
        },
        {
          path: 'vehicles',
          name: 'vehicles',
          component: VehiclesView,
          meta: { title: '车辆档案', permission: 'vehicle:read' },
        },
        {
          path: 'vehicles/new',
          name: 'vehicle-create',
          component: VehicleEditorView,
          meta: { title: '新增车辆', permission: 'vehicle:create' },
        },
        {
          path: 'vehicles/:id',
          name: 'vehicle-detail',
          component: VehicleDetailView,
          meta: { title: '车辆详情', permission: 'vehicle:read' },
        },
        {
          path: 'vehicles/:id/edit',
          name: 'vehicle-edit',
          component: VehicleEditorView,
          meta: { title: '编辑车辆', permission: 'vehicle:update' },
        },
        {
          path: 'energy',
          name: 'energy',
          component: EnergyView,
          meta: { title: '补能管理', permission: 'energy:read' },
        },
        {
          path: 'energy/new',
          name: 'energy-create',
          component: EnergyEditorView,
          meta: { title: '新增补能记录', permission: 'energy:create' },
        },
        {
          path: 'energy/:id',
          name: 'energy-detail',
          component: EnergyDetailView,
          meta: { title: '补能记录详情', permission: 'energy:read' },
        },
        {
          path: 'energy/:id/edit',
          name: 'energy-edit',
          component: EnergyEditorView,
          meta: { title: '编辑补能记录', permission: 'energy:read' },
        },
        {
          path: 'maintenance',
          name: 'maintenance',
          component: MaintenanceView,
          meta: { title: '维修保养', permission: 'maintenance:read' },
        },
        {
          path: 'maintenance/new',
          name: 'maintenance-create',
          component: MaintenanceEditorView,
          meta: { title: '新增维修保养记录', permission: 'maintenance:create' },
        },
        {
          path: 'maintenance/:id',
          name: 'maintenance-detail',
          component: MaintenanceDetailView,
          meta: { title: '维修保养记录详情', permission: 'maintenance:read' },
        },
        {
          path: 'maintenance/:id/edit',
          name: 'maintenance-edit',
          component: MaintenanceEditorView,
          meta: { title: '编辑维修保养记录', permission: 'maintenance:read' },
        },
        {
          path: 'insurance',
          name: 'insurance',
          component: InsuranceView,
          meta: { title: '保险管理', permission: 'insurance:read' },
        },
        {
          path: 'insurance/new',
          name: 'insurance-create',
          component: InsuranceEditorView,
          meta: { title: '新增投保记录', permission: 'insurance:create', hideHeaderTitle: true },
        },
        {
          path: 'insurance/:id',
          name: 'insurance-detail',
          component: InsuranceDetailView,
          meta: { title: '保险记录详情', permission: 'insurance:read' },
        },
        {
          path: 'insurance/:id/edit',
          name: 'insurance-edit',
          component: InsuranceEditorView,
          meta: { title: '编辑保险记录', permission: 'insurance:read', hideHeaderTitle: true },
        },
        {
          path: 'data-exchange',
          name: 'data-exchange',
          component: DataExchangeView,
          meta: { title: '数据导入导出', permission: 'data:import' },
        },
        {
          path: 'system',
          name: 'system',
          component: SystemManagementView,
          meta: { title: '系统管理', permission: 'user:manage' },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore(pinia)
  await auth.restore()

  if (to.meta.public) {
    if (auth.isAuthenticated && to.name === 'login') {
      return auth.user?.mustChangePassword ? '/change-password' : auth.user?.defaultRoute
    }
    return true
  }
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (auth.user?.mustChangePassword && to.name !== 'change-password') {
    return { name: 'change-password' }
  }
  if (to.meta.permission && !auth.can(to.meta.permission)) {
    return auth.user?.defaultRoute ?? '/vehicles'
  }
  return true
})

router.afterEach((to) => {
  document.title = to.meta.title
    ? `${String(to.meta.title)} - 徐州港华车辆管理系统`
    : '徐州港华车辆管理系统'
})
